import { useState, type ReactNode } from 'react';
import { CircleAlert, Plus, Tag as TagIcon } from 'lucide-react';
import { Button, Skeleton, TagChip } from '@/components/ui';
import { TagEditor } from '@/components/tags/TagEditor';
import { useRoles } from '@/hooks/useAuth';
import { useTags } from '@/hooks/useTags';
import type { TagSelection } from '@/types/tags';
import { errorText } from '@/utils/errors';
import { can } from '@/utils/permissions';

/**
 * The tag filter bar above the site list.
 *
 * Clicking a tag narrows the list; clicking it again widens it back. Selected
 * tags combine with AND, because an operator picking two of them is describing
 * one group of sites, not two.
 */
export function TagBar({
  selection,
  onChange,
}: {
  readonly selection: TagSelection;
  readonly onChange: (selection: TagSelection) => void;
}): ReactNode {
  const roles = useRoles();
  const tags = useTags();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const mayManage = can(roles, 'tags.manage');

  if (tags.isPending) {
    return (
      <div className="tag-bar">
        <Skeleton width={120} height={24} radius="md" />
        <Skeleton width={90} height={24} radius="md" />
        <Skeleton width={104} height={24} radius="md" />
      </div>
    );
  }

  if (tags.isError) {
    return (
      <p className="field-error" role="alert">
        <CircleAlert size={14} aria-hidden="true" />
        {errorText(tags.error, 'Теги не загрузились')}
      </p>
    );
  }

  const items = tags.data ?? [];

  const toggle = (id: string): void => {
    const next = selection.tagIds.includes(id) ? selection.tagIds.filter((tagId) => tagId !== id) : [...selection.tagIds, id];

    // Picking a tag and asking for untagged sites at once describes nothing.
    onChange({ tagIds: next, untagged: false });
  };

  return (
    <div className="tag-bar">
      {mayManage ? (
        <span className="popover-anchor">
          <Button size="sm" icon={Plus} onClick={() => setCreating(true)}>
            Новый тег
          </Button>
          <TagEditor open={creating} onClose={() => setCreating(false)} onDeleted={() => setCreating(false)} />
        </span>
      ) : null}

      <button
        type="button"
        className={`tag-chip tag-slate tag-chip-button ${selection.untagged ? 'tag-chip-selected' : ''}`}
        aria-pressed={selection.untagged}
        onClick={() => onChange({ tagIds: [], untagged: !selection.untagged })}
      >
        <TagIcon size={12} aria-hidden="true" />
        <span className="tag-name">Без тегов</span>
      </button>

      {items.length > 0 ? <span className="tag-bar-divider" aria-hidden="true" /> : null}

      {items.map((tag) => (
        <span key={tag.id} className="popover-anchor">
          <TagChip
            tag={tag}
            count={tag.siteCount}
            selected={selection.tagIds.includes(tag.id)}
            onClick={() => toggle(tag.id)}
            {...(mayManage ? { onEdit: () => setEditing(tag.id) } : {})}
          />
          {mayManage ? (
            <TagEditor
              open={editing === tag.id}
              tag={tag}
              onClose={() => setEditing(null)}
              onDeleted={() => {
                setEditing(null);
                // A deleted tag cannot stay in the filter, or the list would
                // keep asking for rows that can no longer match.
                onChange({ ...selection, tagIds: selection.tagIds.filter((id) => id !== tag.id) });
              }}
            />
          ) : null}
        </span>
      ))}

      {selection.tagIds.length > 0 || selection.untagged ? (
        <Button size="sm" variant="ghost" onClick={() => onChange({ tagIds: [], untagged: false })}>
          Сбросить
        </Button>
      ) : null}
    </div>
  );
}
